import React, { useState, useEffect } from 'react';
import './EntryShareModal.css';

const EXPIRY_OPTIONS = [
  { value: '5min',  label: '5 minutos' },
  { value: '30min', label: '30 minutos' },
  { value: '1h',    label: '1 hora' },
  { value: '4h',    label: '4 horas' },
  { value: '24h',   label: '24 horas' },
  { value: '48h',   label: '48 horas' },
  { value: 'never', label: 'Nunca' },
];

function isExpired(expiresAt) {
  return !!expiresAt && new Date() > new Date(expiresAt);
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return 'Sem expiração';
  const d = new Date(expiresAt);
  return isExpired(expiresAt)
    ? `Expirou em ${d.toLocaleString('pt-BR')}`
    : `Expira em ${d.toLocaleString('pt-BR')}`;
}

function getEntryLabel(entry, store) {
  if (!entry) return '—';
  if (entry.customName) return entry.customName;
  if (entry.label) return entry.label;
  if (entry.metadataId && store?.getMetadataName) return store.getMetadataName(entry.metadataId);
  return '—';
}

function fmt(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Math.abs(value ?? 0));
}

// Build initial per-entry settings for a given entry object
function buildInitialSettings(entry) {
  const vis = {};
  (entry.attachments || []).forEach(att => { vis[att.s3Key] = true; });
  return { selected: false, showDueDate: true, attachmentVisibility: vis };
}

const EntryShareModal = ({ isOpen, onClose, entry, allEntries = [], noteId, store }) => {
  // entrySettings: { [entryId]: { selected, showDueDate, attachmentVisibility } }
  const [entrySettings, setEntrySettings] = useState({});
  const [label, setLabel] = useState('');
  const [usePIN, setUsePIN] = useState(false);
  const [pin, setPin] = useState('');
  const [expiresIn, setExpiresIn] = useState('24h');
  const [autoAddNewEntries, setAutoAddNewEntries] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [existingShares, setExistingShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [renewingId, setRenewingId] = useState(null);   // shareId currently being renewed
  const [renewExpiresIn, setRenewExpiresIn] = useState('24h');

  // Only entries that have attachments can be included
  const entriesWithAttachments = allEntries.filter(e => e.attachments?.length > 0);

  useEffect(() => {
    if (!isOpen) return;

    // Init settings: pre-select the clicked entry (if it has attachments)
    const settings = {};
    entriesWithAttachments.forEach(e => {
      settings[e.id] = buildInitialSettings(e);
    });
    if (entry?.id && settings[entry.id]) {
      settings[entry.id].selected = true;
    }
    setEntrySettings(settings);
    setCreated(null);
    setError('');
    setLabel('');
    setUsePIN(false);
    setPin('');
    setExpiresIn('24h');
    setAutoAddNewEntries(false);

    setLoadingShares(true);
    store.listEntryShares(noteId)
      .then(r => setExistingShares(r.shares || []))
      .catch(() => {})
      .finally(() => setLoadingShares(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entry?.id, noteId]);

  const handleRenew = async (shareId) => {
    try {
      const updated = await store.updateEntryShare(shareId, { expiresIn: renewExpiresIn });
      setExistingShares(prev => prev.map(s => s.id === shareId ? { ...s, ...updated } : s));
      if (created?.id === shareId) setCreated(prev => ({ ...prev, ...updated }));
    } catch (e) {
      alert('Erro ao renovar link: ' + e.message);
    }
    setRenewingId(null);
  };

  const allSelected = entriesWithAttachments.length > 0 &&
    entriesWithAttachments.every(e => entrySettings[e.id]?.selected);

  const toggleAllEntries = () => {
    const next = !allSelected;
    setEntrySettings(prev => {
      const updated = { ...prev };
      entriesWithAttachments.forEach(e => { updated[e.id] = { ...updated[e.id], selected: next }; });
      return updated;
    });
  };

  const toggleEntry = (entryId) => {
    setEntrySettings(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], selected: !prev[entryId].selected }
    }));
  };

  const toggleShowDueDate = (entryId) => {
    setEntrySettings(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], showDueDate: !prev[entryId].showDueDate }
    }));
  };

  const toggleAttachment = (entryId, s3Key) => {
    setEntrySettings(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        attachmentVisibility: {
          ...prev[entryId].attachmentVisibility,
          [s3Key]: !prev[entryId].attachmentVisibility[s3Key]
        }
      }
    }));
  };

  const selectedEntries = entriesWithAttachments.filter(e => entrySettings[e.id]?.selected);

  const handleSubmit = async () => {
    if (selectedEntries.length === 0) {
      setError('Selecione pelo menos uma entrada.');
      return;
    }
    if (usePIN && !/^\d{4}$/.test(pin)) {
      setError('O PIN deve ter exatamente 4 dígitos.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const entries = selectedEntries.map(e => {
        const s = entrySettings[e.id];
        return { entryId: e.id, showDueDate: s.showDueDate, attachmentVisibility: s.attachmentVisibility };
      });
      const result = await store.createEntryShare(noteId, {
        entries,
        label: label.trim() || undefined,
        password: usePIN ? pin : null,
        expiresIn,
        autoAddNewEntries
      });
      setCreated(result);
      setExistingShares(prev => [result, ...prev]);
    } catch (e) {
      setError(e.message || 'Erro ao criar link.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDeleteShare = async (shareId) => {
    try {
      await store.deleteEntryShare(shareId);
      setExistingShares(prev => prev.filter(s => s.id !== shareId));
      if (created?.id === shareId) setCreated(null);
    } catch (e) {
      alert('Erro ao remover link: ' + e.message);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="entry-share-modal">
        <div className="modal-header">
          <h3>
            <i className="fa-solid fa-share-nodes" style={{ marginRight: 8, color: '#2196f3' }}></i>
            Compartilhar comprovantes
          </h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">

          {/* Entry selection */}
          <div className="form-group">
            <label>Entradas incluídas no link</label>
            <span className="helper-text">Apenas entradas com anexos podem ser compartilhadas.</span>
            {entriesWithAttachments.length === 0 ? (
              <p style={{ color: '#888', fontSize: '0.85rem', margin: '8px 0 0' }}>
                Nenhuma entrada com anexo nesta nota.
              </p>
            ) : (
              <>
                <label className="toggle-label" style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAllEntries}
                  />
                  Todos
                </label>
              <div className="entry-selection-list">
                {entriesWithAttachments.map(e => {
                  const s = entrySettings[e.id] || {};
                  const eLabel = getEntryLabel(e, store);
                  return (
                    <div key={e.id} className={`entry-selection-item${s.selected ? ' selected' : ''}`}>
                      {/* Row: checkbox + label + value */}
                      <div className="entry-selection-row" onClick={() => toggleEntry(e.id)}>
                        <input
                          type="checkbox"
                          checked={!!s.selected}
                          onChange={() => toggleEntry(e.id)}
                          onClick={ev => ev.stopPropagation()}
                        />
                        <span className="entry-selection-name">{eLabel}</span>
                        <span className={`entry-selection-value ${e.value < 0 ? 'negative' : 'positive'}`}>
                          {e.value < 0 ? '−' : '+'} {fmt(e.value)}
                        </span>
                      </div>

                      {/* Visibility options — only when selected */}
                      {s.selected && (
                        <div className="visibility-list" style={{ marginTop: 8 }}>
                          <div className="visibility-item">
                            <span>📝 Título / Descrição</span>
                            <span className="visibility-item-always">sempre visível</span>
                          </div>
                          <div className="visibility-item">
                            <span>📅 Vencimento</span>
                            <label>
                              <input
                                type="checkbox"
                                checked={!s.showDueDate}
                                onChange={() => toggleShowDueDate(e.id)}
                              />
                              ocultar
                            </label>
                          </div>
                          {(e.attachments || []).map((att, i) => (
                            <div className="visibility-item" key={att.s3Key}>
                              <span>
                                <i className="fa-solid fa-paperclip"></i>
                                {att.displayName || att.filename || `Arquivo ${i + 1}`}
                              </span>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={s.attachmentVisibility[att.s3Key] === false}
                                  onChange={() => toggleAttachment(e.id, att.s3Key)}
                                />
                                ocultar
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </div>

          {/* Existing shares */}
          {loadingShares ? (
            <div style={{ fontSize: '0.85rem', color: '#999' }}>Carregando links...</div>
          ) : existingShares.length > 0 && (
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#444', marginBottom: 6 }}>
                Links criados
              </div>
              <div className="existing-shares">
                {existingShares.map(s => {
                  const expired = isExpired(s.expiresAt);
                  const entryCount = s.entryCount ?? s.entries?.length ?? '?';
                  return (
                    <div className={`existing-share-item${expired ? ' expired' : ''}`} key={s.id}>
                      {/* Top row: icon + URL + badges + actions */}
                      <div className="share-item-top">
                        <i className={`fa-solid fa-link share-item-icon${expired ? ' expired' : ''}`}></i>
                        <code title={s.shareUrl}>{s.shareUrl}</code>
                        <div className="share-item-badges">
                          {expired
                            ? <span className="share-badge expired">Expirado</span>
                            : <span className="share-badge active">Ativo</span>}
                          {s.passwordProtected && <span className="share-badge pin">🔒 PIN</span>}
                        </div>
                        <button className="copy-btn" onClick={() => handleCopy(s.shareUrl)} title="Copiar link" disabled={expired}>
                          <i className="fa-regular fa-copy"></i>
                        </button>
                        <button className="btn-danger" onClick={() => handleDeleteShare(s.id)} title="Remover">
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                      {/* Meta row */}
                      <div className="share-item-meta">
                        <span>{entryCount} {entryCount === 1 ? 'entrada' : 'entradas'}{s.autoAddNewEntries && !expired ? ' ⚡' : ''}</span>
                        <span>·</span>
                        <span>{s.accessCount ?? 0} {(s.accessCount ?? 0) === 1 ? 'acesso' : 'acessos'}</span>
                        <span>·</span>
                        <span className={expired ? 'meta-expired' : ''}>{formatExpiry(s.expiresAt)}</span>
                      </div>
                      {/* Auto-add toggle row — only for active links */}
                      {!expired && (
                        <label className="share-auto-add-toggle">
                          <input
                            type="checkbox"
                            checked={!!s.autoAddNewEntries}
                            onChange={async () => {
                              const next = !s.autoAddNewEntries;
                              setExistingShares(prev => prev.map(x => x.id === s.id ? { ...x, autoAddNewEntries: next } : x));
                              try {
                                await store.updateEntryShare(s.id, { autoAddNewEntries: next });
                                if (next) {
                                  // Backfill: add all entries with attachments not yet in this share
                                  let updated;
                                  for (const e of entriesWithAttachments) {
                                    updated = await store.updateEntryShare(s.id, { addEntry: { entryId: e.id } });
                                  }
                                  if (updated) {
                                    setExistingShares(prev => prev.map(x => x.id === s.id ? { ...x, ...updated, autoAddNewEntries: true } : x));
                                  }
                                }
                              } catch {
                                setExistingShares(prev => prev.map(x => x.id === s.id ? { ...x, autoAddNewEntries: !next } : x));
                              }
                            }}
                          />
                          Adicionar novos anexos automaticamente
                        </label>
                      )}
                      {/* Renew row */}
                      {renewingId === s.id ? (
                        <div className="share-item-renew">
                          <select
                            value={renewExpiresIn}
                            onChange={e => setRenewExpiresIn(e.target.value)}
                            className="renew-select"
                          >
                            {EXPIRY_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                          <button className="btn-primary renew-confirm" onClick={() => handleRenew(s.id)}>
                            Confirmar
                          </button>
                          <button className="btn-secondary" onClick={() => setRenewingId(null)}>
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          className="share-renew-btn"
                          onClick={() => { setRenewingId(s.id); setRenewExpiresIn('24h'); }}
                        >
                          <i className="fa-solid fa-rotate-right"></i>
                          {expired ? 'Reativar link' : 'Alterar expiração'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Success box */}
          {created && (
            <div className="success-box">
              <p>✅ Link criado com sucesso! ({created.entryCount} {created.entryCount === 1 ? 'entrada' : 'entradas'})</p>
              <div className="share-url-row">
                <code title={created.shareUrl}>{created.shareUrl}</code>
                <button className="copy-btn" onClick={() => handleCopy(created.shareUrl)}>
                  {copied ? '✓ Copiado' : 'Copiar'}
                </button>
              </div>
              {created.expiresAt && (
                <div style={{ fontSize: '0.82rem', color: '#555' }}>
                  Expira em: {new Date(created.expiresAt).toLocaleString('pt-BR')}
                </div>
              )}
              {created.passwordProtected && (
                <div style={{ fontSize: '0.82rem', color: '#555' }}>🔒 Protegido por PIN</div>
              )}
            </div>
          )}

          {existingShares.length > 0 && (
            <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: 4 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#444' }}>Criar novo link</div>
            </div>
          )}

          {/* Label */}
          <div className="form-group">
            <label>Identificador do link (opcional)</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="ex: contas-maio"
            />
            <span className="helper-text">Deixe vazio para link aleatório. Será convertido para slug.</span>
          </div>

          {/* PIN */}
          <div className="form-group">
            <div className="pin-row">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={usePIN}
                  onChange={e => { setUsePIN(e.target.checked); setPin(''); }}
                />
                Proteger com PIN de 4 dígitos
              </label>
              {usePIN && (
                <input
                  type="number"
                  className="pin-input"
                  value={pin}
                  onChange={e => setPin(e.target.value.slice(0, 4))}
                  placeholder="0000"
                  min="0"
                  max="9999"
                />
              )}
            </div>
          </div>

          {/* Expiry */}
          <div className="form-group">
            <label>Validade do link</label>
            <select value={expiresIn} onChange={e => setExpiresIn(e.target.value)}>
              {EXPIRY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Auto add new entries */}
          <div className="form-group">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={autoAddNewEntries}
                onChange={e => setAutoAddNewEntries(e.target.checked)}
              />
              Adicionar novos anexos automaticamente
            </label>
            <span className="helper-text">Quando novos arquivos forem adicionados a entradas desta nota, elas serão incluídas neste link automaticamente.</span>
          </div>

          {error && <p className="error-msg">{error}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fechar</button>
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading || selectedEntries.length === 0}
          >
            {loading ? 'Gerando...' : `Gerar link${selectedEntries.length > 0 ? ` (${selectedEntries.length})` : ''}`}
          </button>
        </div>
      </div>
    </>
  );
};

export default EntryShareModal;
